import type { GroupPublic } from '@repo/types/groups';
import type { UserPublic } from '@repo/types/users';

import { useEditChat } from '@repo/api/chat/useEditChat';
import { useGetChat } from '@repo/api/chat/useGetChat';
import { useGetCreateConversationUsersInfinite } from '@repo/api/chat/useGetCreateConversationUsersInfinite';
import { useGetAssignedGroupsInfinite } from '@repo/api/group/useGetAssignedGroupsInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { CheckCircle2Icon, UserIcon, UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tags';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the ChatUsersEdit component.
 * @property {number} id - The chat ID to edit users for.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface ChatUsersEditProps {
  id: number;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function ChatUsersEdit(chatUsersEditProps: ChatUsersEditProps) {
  const { id, open, onOpenChange } = chatUsersEditProps;
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounceValue(searchValue, 400);
  const [selectedUsers, setSelectedUsers] = useState<UserPublic[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupPublic[]>([]);

  const { mutateAsync, isPending: editIsPending } = useEditChat();

  const getConversationQuery = useGetChat(id, open);

  const { errorMessage: usersErrorMessage, ...getUsersInfiniteQuery } = useGetCreateConversationUsersInfinite({
    name: searchDebouncedValue,
  });

  const { data: users, isEmpty: usersIsEmpty, noResults: usersNoResults } = useInfiniteQueryResult(getUsersInfiniteQuery.data, searchDebouncedValue);

  const { errorMessage: groupsErrorMessage, ...getAssignedGroupsInfiniteQuery } = useGetAssignedGroupsInfinite(searchDebouncedValue);

  const { data: groups, isEmpty: groupsIsEmpty, noResults: groupsNoResults } = useInfiniteQueryResult(getAssignedGroupsInfiniteQuery.data, searchDebouncedValue);

  const onClose = () => {
    onOpenChange(false);
    setSelectedUsers([]);
    setSelectedGroups([]);
  };

  const onSubmit = async () => {
    if (!getConversationQuery.isSuccess) {
      return;
    }
    const { id, name, users = [], groups = [] } = getConversationQuery.data;
    const userIds = selectedUsers.map((u) => u.id);
    const groupIds = selectedGroups.map((g) => g.id);

    const addUserIdArr = userIds.filter((userId) => !users.some((user) => user.id === userId));
    const addGroupIdArr = groupIds.filter((groupId) => !groups.some((group) => group.id === groupId));
    const removeUserIdArr = users.filter((user) => !userIds.some((userId) => user.id === userId)).map((user) => user.id);
    const removeGroupIdArr = groups.filter((group) => !groupIds.some((groupId) => group.id === groupId)).map((group) => group.id);

    await mutateAsync({
      id: id,
      name: name,
      addUserIdArr: addUserIdArr,
      addGroupIdArr: addGroupIdArr,
      removeUserIdArr: removeUserIdArr,
      removeGroupIdArr: removeGroupIdArr,
    }).then(() => {
      onClose();
    });
  };

  const onRemoveUser = (id: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
  };
  const onRemoveGroup = (id: number) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== id));
  };

  useEffect(() => {
    if (getConversationQuery.isSuccess) {
      const { users, groups } = getConversationQuery.data;
      setSelectedUsers(users);
      setSelectedGroups(groups);
    }
  }, [open, getConversationQuery.isSuccess, getConversationQuery.data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <LoadingOverlay isLoading={editIsPending}>
          <LoadingOverlaySpinner />
          <LoadingOverlayLabel>Trwa zapisywanie zmian...</LoadingOverlayLabel>
        </LoadingOverlay>
        <LoadingOverlay isLoading={getConversationQuery.isPending}>
          <LoadingOverlaySpinner />
          <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
        </LoadingOverlay>
        <DialogBody>
          <DialogHeader className={'border-border-primary border-b py-5'}>
            <DialogTitle>Edytuj użytkowników</DialogTitle>
          </DialogHeader>
          <DialogDescription className={'sr-only'}>Formularz edycji użytkowników wybranego chatu</DialogDescription>
          <Tabs className={'flex grow flex-col gap-3 overflow-y-auto p-4'} defaultValue={'users'}>
            <div className={'flex flex-col gap-4'}>
              <TabsList className={'grid w-full grid-cols-2'}>
                <TabsTrigger value={'users'}>Użytkownicy</TabsTrigger>
                <TabsTrigger value={'groups'}>Grupy</TabsTrigger>
              </TabsList>
              <SearchInput setValue={setSearchValue} placeholder="Wyszukaj..." />
              <div className="flex flex-wrap gap-1">
                {selectedUsers.map(({ id, name, surname, avatarURL }) => (
                  <Tag key={id} onRemove={() => onRemoveUser(id)} variant={'muted'} border>
                    <Avatar size={'xs'}>
                      <AvatarImage src={getAvatar(avatarURL)} alt={name + '_' + surname} />
                      <AvatarFallback>
                        <UserIcon />
                      </AvatarFallback>
                    </Avatar>
                    {name} {surname?.[0] ? `${surname[0]}.` : ''}
                  </Tag>
                ))}
                {selectedGroups.map(({ id, name, avatarURL }) => (
                  <Tag key={id} onRemove={() => onRemoveGroup(id)} variant={'muted'} border>
                    <Avatar size={'xs'}>
                      <AvatarImage src={getAvatar(avatarURL)} alt={name} />
                      <AvatarFallback>
                        <UsersIcon />
                      </AvatarFallback>
                    </Avatar>
                    {name}
                  </Tag>
                ))}
              </div>
            </div>
            <TabsContent value={'users'}>
              <SelectList
                type={'multiple'}
                selected={selectedUsers.map((u) => String(u.id))}
                setSelected={(ids) => {
                  const selectedIds = new Set(ids.map(Number));
                  const updatedUsers = [...selectedUsers.filter((u) => selectedIds.has(u.id)), ...users.filter((u) => selectedIds.has(u.id) && !selectedUsers.some((s) => s.id === u.id))];
                  setSelectedUsers(updatedUsers);
                }}
              >
                <SelectListInfiniteQueryStates
                  query={getUsersInfiniteQuery}
                  isEmpty={usersIsEmpty}
                  noResults={usersNoResults}
                  isEmptyMessage={'Wyszukaj użytkownika'}
                  noResultMessage={'Nie znaleziono użytkownika'}
                  errorMessage={usersErrorMessage}
                />

                {users.map((user) => (
                  <SelectListElement key={user.id} id={String(user.id)}>
                    <Avatar size={'sm'}>
                      <AvatarImage src={getAvatar(user.avatarURL)} alt={user.name} />
                      <AvatarFallback>{user.name[0]}</AvatarFallback>
                    </Avatar>
                    <SelectListElementContent>
                      <SelectListElementTitle>
                        {user.name} {user.surname}
                      </SelectListElementTitle>
                      <SelectListElementDescription>{getUserLvl(user.lvl, user.accountType)}</SelectListElementDescription>
                    </SelectListElementContent>

                    <SelectListElementTrigger>
                      {({ selected, toggleElement }) => (
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => {
                            toggleElement();
                          }}
                          className="after:absolute after:inset-0"
                        />
                      )}
                    </SelectListElementTrigger>
                  </SelectListElement>
                ))}
                <InfiniteScroll
                  hasNextPage={getUsersInfiniteQuery.hasNextPage}
                  isLoading={getUsersInfiniteQuery.isLoading}
                  isFetching={getUsersInfiniteQuery.isFetching}
                  next={getUsersInfiniteQuery.fetchNextPage}
                  threshold={0.65}
                >
                  {getUsersInfiniteQuery.hasNextPage && <SelectListElementSkeleton />}
                </InfiniteScroll>
              </SelectList>
            </TabsContent>
            <TabsContent value={'groups'}>
              <SelectList
                type={'multiple'}
                selected={selectedGroups.map((g) => String(g.id))}
                setSelected={(ids) => {
                  const selectedIds = new Set(ids.map(Number));
                  const updatedGroups = [...selectedGroups.filter((g) => selectedIds.has(g.id)), ...groups.filter((g) => selectedIds.has(g.id) && !selectedGroups.some((s) => s.id === g.id))];
                  setSelectedGroups(updatedGroups);
                }}
              >
                <SelectListInfiniteQueryStates
                  query={getAssignedGroupsInfiniteQuery}
                  isEmpty={groupsIsEmpty}
                  noResults={groupsNoResults}
                  isEmptyMessage={'Wyszukaj grupy'}
                  noResultMessage={'Nie znaleziono grupy'}
                  errorMessage={groupsErrorMessage}
                />

                {groups.map((group) => (
                  <SelectListElement key={group.id} id={String(group.id)}>
                    <Avatar size={'sm'}>
                      <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} />
                      <AvatarFallback>{group.name[0]}</AvatarFallback>
                    </Avatar>
                    <SelectListElementContent>
                      <SelectListElementTitle>{group.name}</SelectListElementTitle>
                      <SelectListElementDescription>Grupa</SelectListElementDescription>
                    </SelectListElementContent>

                    <SelectListElementTrigger>
                      {({ selected, toggleElement }) => (
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => {
                            toggleElement();
                          }}
                          className="after:absolute after:inset-0"
                        />
                      )}
                    </SelectListElementTrigger>
                  </SelectListElement>
                ))}
                <InfiniteScroll
                  hasNextPage={getAssignedGroupsInfiniteQuery.hasNextPage}
                  isLoading={getAssignedGroupsInfiniteQuery.isLoading}
                  isFetching={getAssignedGroupsInfiniteQuery.isFetching}
                  next={getAssignedGroupsInfiniteQuery.fetchNextPage}
                  threshold={0.65}
                >
                  {getAssignedGroupsInfiniteQuery.hasNextPage && <SelectListElementSkeleton />}
                </InfiniteScroll>
              </SelectList>
            </TabsContent>
          </Tabs>
        </DialogBody>
        <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
            Anuluj
          </Button>
          <Button disabled={(selectedUsers.length === 0 && selectedGroups.length === 0) || editIsPending} onClick={onSubmit} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
            <LoadingSwap isLoading={editIsPending} className="flex items-center gap-2">
              <CheckCircle2Icon />
              Zapisz
            </LoadingSwap>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
